import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Download, Eye, Spinner, FileText, Image as ImageIcon, FilmStrip, ArrowSquareOut, MagnifyingGlass, PencilSimple, Check, FilePdf, FileZip, FileCsv, FileXls, FileDoc } from "@phosphor-icons/react";
import { fetchCompressorFiles, resolveCompressorFileUrl, updateCompressorFile } from "@/lib/api";
import { extractErr } from "@/lib/errors";
import { toast } from "sonner";

const IMG_EXT = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "heic", "heif", "svg"];
const PDF_EXT = ["pdf"];
const VIDEO_EXT = ["mp4", "mov", "avi", "wmv", "mkv", "webm", "m4v", "3gp", "mpg", "mpeg"];

/**
 * CompressorFilesModal — lists every file/picture attached to a compressor's serial.
 * Mirrors PartFilesModal: DB-driven listing + lazy Drive resolution on click, hover thumbnail.
 */
export default function CompressorFilesModal({ compressor, onClose, user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!compressor?.serial_id) return;
    setLoading(true); setErr("");
    fetchCompressorFiles(compressor.serial_id)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load files"))
      .finally(() => setLoading(false));
  }, [compressor?.serial_id]);

  const items = data?.items || [];
  const filtered = items.filter((it) => {
    const q = search.trim().toLowerCase();
    const matchesQ = !q || (
      (it.description || "").toLowerCase().includes(q) ||
      (it.entries || []).some((e) => (e.filename || "").toLowerCase().includes(q))
    );
    const matchesKind = kindFilter === "all" || (it.entries || []).some((e) => e.kind === kindFilter);
    return matchesQ && matchesKind;
  });

  const counts = data?.counts || { picture: 0, video: 0, file: 0 };

  const body = (
    <div className="fixed inset-0 z-[1000] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-2 md:p-6"
      onClick={onClose} data-testid="compressor-files-modal">
      <div className="bg-white rounded-sm w-full max-w-5xl h-full max-h-[92vh] shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center shrink-0">
            <FileText size={20} weight="bold" className="text-blue-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Products Sold — Files</div>
            <div className="text-base font-bold font-['Chivo'] text-slate-900 truncate">
              Serial #{compressor?.serial_number} <span className="text-slate-500 font-normal">· {compressor?.product_name}</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="compressor-files-close"
            className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </header>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/50">
          <div className="flex gap-1.5 flex-wrap">
            <Chip label="All" count={items.length} active={kindFilter === "all"} onClick={() => setKindFilter("all")} testId="cf-filter-all" />
            <Chip label="Pictures" count={counts.picture || 0} active={kindFilter === "picture"} onClick={() => setKindFilter("picture")} testId="cf-filter-picture" icon={ImageIcon} />
            <Chip label="Videos" count={counts.video || 0} active={kindFilter === "video"} onClick={() => setKindFilter("video")} testId="cf-filter-video" icon={FilmStrip} />
            <Chip label="Files" count={counts.file || 0} active={kindFilter === "file"} onClick={() => setKindFilter("file")} testId="cf-filter-file" icon={FileText} />
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-md ml-auto">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="compressor-files-search"
              placeholder="Search description or filename…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 md:p-5 bg-slate-50/40 relative">
          {loading && (
            <div className="flex items-center justify-center py-20 text-slate-500 text-sm gap-2" data-testid="compressor-files-loading">
              <Spinner size={16} className="animate-spin" /> Loading files…
            </div>
          )}
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm p-3" data-testid="compressor-files-error">{err}</div>}
          {!loading && !err && filtered.length === 0 && (
            <div className="text-center py-16 text-sm text-slate-500" data-testid="compressor-files-empty">
              No files for this compressor{search ? " match your search" : ""}.
            </div>
          )}
          {!loading && !err && filtered.length > 0 && (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {filtered.map((row) => (
                <CompressorFileRow key={row.id} row={row} serialId={compressor?.serial_id}
                  driveConfigured={data?.drive_configured} onPreview={setPreview}
                  isAdmin={isAdmin} userEmail={user?.email}
                  isEditing={editingId === row.id}
                  onEditStart={() => setEditingId(row.id)}
                  onEditCancel={() => setEditingId(null)}
                  onEditSaved={(updated) => {
                    setEditingId(null);
                    setData((d) => ({
                      ...d,
                      items: d.items.map((it) => it.id === row.id ? { ...it, ...updated } : it),
                    }));
                  }} />
              ))}
            </ul>
          )}

          {preview && <PreviewOverlay preview={preview} onClose={() => setPreview(null)} />}
        </div>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}

function Chip({ label, count, active, onClick, icon: Icon, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-sm border ${
        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}>
      {Icon && <Icon size={12} weight="bold" />}
      {label}
      <span className={`text-[10px] font-bold ${active ? "text-white/70" : "text-slate-500"}`}>{count}</span>
    </button>
  );
}

function iconForExt(ext, kind) {
  if (kind === "picture") return ImageIcon;
  if (kind === "video") return FilmStrip;
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return FilePdf;
  if (["zip", "rar", "7z"].includes(e)) return FileZip;
  if (e === "csv") return FileCsv;
  if (["xls", "xlsx"].includes(e)) return FileXls;
  if (["doc", "docx"].includes(e)) return FileDoc;
  return FileText;
}

function CompressorFileRow({ row, serialId, driveConfigured, onPreview, isAdmin, userEmail, isEditing, onEditStart, onEditCancel, onEditSaved }) {
  const [editDesc, setEditDesc] = useState(row.description || "");
  const [editDate, setEditDate] = useState(String(row.date || "").slice(0, 10));
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setEditDesc(row.description || "");
    setEditDate(String(row.date || "").slice(0, 10));
    onEditStart();
  };
  const save = async () => {
    setSaving(true);
    try {
      await updateCompressorFile(serialId, row.id, {
        description: editDesc,
        date: editDate || "",
        requester_email: userEmail,
      });
      onEditSaved({ description: editDesc, date: editDate || null });
    } catch (e) {
      toast.error(extractErr(e, "Save failed").message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="bg-white border border-slate-200 rounded-sm p-2 md:p-3 hover:border-blue-300 hover:shadow-sm transition-all"
      data-testid={`compressor-file-row-${row.id}`}>
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
              <input value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
                data-testid={`compressor-file-edit-desc-${row.id}`}
                className="w-full text-sm font-semibold text-slate-900 border border-blue-400 focus:ring-1 focus:ring-blue-500 rounded-sm px-1.5 py-1 outline-none"
                placeholder="Description" />
              <input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)}
                data-testid={`compressor-file-edit-date-${row.id}`}
                className="text-[11px] text-slate-700 border border-slate-300 rounded-sm px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-500" />
              <div className="flex gap-1">
                <button onClick={save} disabled={saving} data-testid={`compressor-file-save-${row.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-2 py-1 rounded-sm disabled:opacity-50">
                  {saving ? <Spinner size={10} className="animate-spin" /> : <Check size={10} weight="bold" />} Save
                </button>
                <button onClick={onEditCancel} disabled={saving} data-testid={`compressor-file-cancel-${row.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded-sm">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="text-xs md:text-sm font-semibold text-slate-900 line-clamp-2" title={row.description}>
                {row.description || "Untitled"}
              </div>
              <div className="text-[10px] text-slate-500 flex items-center gap-1.5 mt-0.5">
                {row.date && <span>{String(row.date).slice(0, 10)}</span>}
                {row.created_by && <><span>·</span><span className="truncate max-w-[180px]" title={row.created_by}>{row.created_by}</span></>}
              </div>
            </>
          )}
        </div>
        {isAdmin && !isEditing && (
          <button onClick={startEdit} data-testid={`compressor-file-edit-${row.id}`}
            className="w-6 h-6 flex items-center justify-center rounded-sm border border-slate-200 bg-white hover:border-blue-400 hover:text-blue-700 text-slate-400 shrink-0"
            title="Edit description & date">
            <PencilSimple size={11} weight="bold" />
          </button>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        {(row.entries || []).map((e, idx) => (
          <Entry key={`${row.id}-${idx}`} entry={e} serialId={serialId} driveConfigured={driveConfigured} onPreview={onPreview} rowId={row.id} />
        ))}
      </div>
    </li>
  );
}

function Entry({ entry, serialId, driveConfigured, onPreview, rowId }) {
  const [resolving, setResolving] = useState(false);
  const [thumbUrl, setThumbUrl] = useState("");
  const [thumbFailed, setThumbFailed] = useState(false);
  const cacheRef = useRef(null);
  const isImg = IMG_EXT.includes(entry.ext);
  const isPdf = PDF_EXT.includes(entry.ext);
  const isVideo = VIDEO_EXT.includes(entry.ext);
  const canPreview = driveConfigured && (isImg || isPdf);
  // Google Drive auto-generates thumbnails for videos too, so we load one for picture/video.
  const wantThumb = driveConfigured && (isImg || isVideo);

  const resolveOnce = async () => {
    if (cacheRef.current) return cacheRef.current;
    const res = await resolveCompressorFileUrl(serialId, entry.path);
    cacheRef.current = res;
    return res;
  };

  // Auto-resolve thumbnail on mount for picture/video entries.
  useEffect(() => {
    if (!wantThumb) return;
    let cancelled = false;
    resolveOnce().then((res) => {
      if (cancelled) return;
      if (res?.resolved && res.thumbnail_url) setThumbUrl(res.thumbnail_url);
      else setThumbFailed(true);
    }).catch(() => { if (!cancelled) setThumbFailed(true); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.path, serialId, wantThumb]);

  const runDownload = async () => {
    setResolving(true);
    try {
      const res = await resolveOnce();
      if (!res?.resolved) { alert(`File not found in Drive: ${entry.filename}`); return; }
      window.open(res.download_url, "_blank", "noopener");
    } finally { setResolving(false); }
  };
  const runPreview = async () => {
    setResolving(true);
    try {
      const res = await resolveOnce();
      if (!res?.resolved) { alert(`File not found in Drive: ${entry.filename}`); return; }
      onPreview({ ext: entry.ext, filename: entry.filename, resolved: res });
    } finally { setResolving(false); }
  };

  const KindIcon = iconForExt(entry.ext, entry.kind);
  const showThumb = wantThumb && thumbUrl && !thumbFailed;

  return (
    <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-sm p-1.5"
      data-testid={`compressor-file-entry-${rowId}-${entry.kind}`}>
      {/* Small file-type icon always at the beginning */}
      <div className="w-6 h-6 shrink-0 rounded-sm bg-white border border-slate-200 flex items-center justify-center text-slate-500">
        <KindIcon size={12} weight="bold" />
      </div>
      {/* Inline thumbnail for pictures + videos (clickable → preview) */}
      {wantThumb && (
        <button type="button"
          onClick={canPreview ? runPreview : runDownload}
          data-testid={`compressor-file-thumb-${rowId}-${entry.kind}`}
          className="w-12 h-12 shrink-0 rounded-sm overflow-hidden border border-slate-200 bg-slate-900 flex items-center justify-center relative group">
          {showThumb ? (
            <img src={thumbUrl} alt={entry.filename}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
              onError={() => setThumbFailed(true)} />
          ) : thumbFailed ? (
            <KindIcon size={18} className="text-slate-400" />
          ) : (
            <Spinner size={14} className="animate-spin text-slate-300" />
          )}
          {isVideo && showThumb && (
            <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
              <FilmStrip size={16} weight="fill" />
            </span>
          )}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-slate-700 truncate font-mono" title={entry.filename}>{entry.filename}</div>
        <div className="text-[9px] text-slate-400 uppercase tracking-wider">{entry.kind} · {entry.ext || "?"}</div>
      </div>

      <div className="flex gap-1 shrink-0">
        {canPreview && (
          <button disabled={resolving} onClick={runPreview} data-testid={`compressor-file-preview-${rowId}-${entry.kind}`}
            className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 px-1.5 py-1 rounded-sm disabled:opacity-50"
            title="Preview">
            {resolving ? <Spinner size={10} className="animate-spin" /> : <Eye size={10} weight="bold" />}
          </button>
        )}
        <button disabled={resolving || !driveConfigured} onClick={runDownload} data-testid={`compressor-file-download-${rowId}-${entry.kind}`}
          className="inline-flex items-center gap-1 text-[10px] font-bold text-white bg-blue-700 hover:bg-blue-800 px-1.5 py-1 rounded-sm disabled:opacity-50"
          title={driveConfigured ? "Download / Open" : "Drive not configured"}>
          {resolving ? <Spinner size={10} className="animate-spin" /> : (isVideo ? <ArrowSquareOut size={10} weight="bold" /> : <Download size={10} weight="bold" />)}
        </button>
      </div>
    </div>
  );
}

function PreviewOverlay({ preview, onClose }) {
  const ext = String(preview.ext || "").toLowerCase();
  const isImg = IMG_EXT.includes(ext);
  const isPdf = PDF_EXT.includes(ext);
  const r = preview.resolved || {};
  return (
    <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-2 md:p-8 z-10"
      onClick={onClose} data-testid="compressor-file-preview">
      <div className="relative bg-white rounded-sm max-w-5xl w-full h-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-200 shrink-0">
          <span className="text-xs font-semibold text-slate-700 truncate flex-1">{preview.filename}</span>
          {r.download_url && (
            <a href={r.download_url} target="_blank" rel="noopener noreferrer"
              className="text-xs font-bold text-blue-700 hover:text-blue-900 flex items-center gap-1">
              <Download size={12} weight="bold" /> Download
            </a>
          )}
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-slate-900 flex items-center justify-center">
          {isImg && r.thumbnail_url ? (
            <img src={r.thumbnail_url} alt={preview.filename} className="max-w-full max-h-full object-contain" />
          ) : isPdf && r.embed_url ? (
            <iframe src={r.embed_url} title={preview.filename} className="w-full h-full bg-white" />
          ) : (
            <div className="text-white text-sm">Preview not supported. Use Download.</div>
          )}
        </div>
      </div>
    </div>
  );
}
